/**
 * Parse lesson video_link values into Vimeo Player embed URLs.
 * Supports player embeds, watch pages, and unlisted privacy hashes.
 * Returns null for non-Vimeo hosts so callers can fall back to a generic iframe.
 */

/**
 * @typedef {Object} ParsedVimeoUrl
 * @property {string} id - Numeric Vimeo video id
 * @property {string|null} hash - Unlisted privacy hash when present
 * @property {string} embedUrl - player.vimeo.com URL suitable as an iframe src
 */

/**
 * Extract a Vimeo video id, optional privacy hash, and embed URL from a free-text link.
 * @param {string} raw - Value stored on lesson.video_link
 * @returns {ParsedVimeoUrl|null}
 */
export const parseVimeoUrl = (raw) => {
  if (!raw || typeof raw !== 'string') return null;

  const trimmed = raw.trim();
  if (!trimmed) return null;

  let url;
  try {
    url = new URL(trimmed);
  } catch {
    return null;
  }

  const host = url.hostname.replace(/^www\./i, '').toLowerCase();
  if (host !== 'vimeo.com' && host !== 'player.vimeo.com') {
    return null;
  }

  const pathParts = url.pathname.split('/').filter(Boolean);
  let id = null;
  let hash = url.searchParams.get('h');

  if (host === 'player.vimeo.com') {
    const videoIndex = pathParts.indexOf('video');
    const candidate = videoIndex >= 0 ? pathParts[videoIndex + 1] : null;
    if (candidate && /^\d+$/.test(candidate)) {
      id = candidate;
    }
    if (!id) return null;
    return {
      id,
      hash,
      embedUrl: url.toString()
    };
  }

  const numericSegments = pathParts.filter((part) => /^\d+$/.test(part));
  if (numericSegments.length === 0) return null;

  id = numericSegments[numericSegments.length - 1];
  const idIndex = pathParts.lastIndexOf(id);
  const nextSegment = pathParts[idIndex + 1];
  if (nextSegment && /^[a-f0-9]+$/i.test(nextSegment) && !/^\d+$/.test(nextSegment)) {
    hash = hash || nextSegment;
  }

  const embed = new URL(`https://player.vimeo.com/video/${id}`);
  url.searchParams.forEach((value, key) => {
    embed.searchParams.set(key, value);
  });
  if (hash) {
    embed.searchParams.set('h', hash);
  }

  return {
    id,
    hash,
    embedUrl: embed.toString()
  };
};

/**
 * Whether a lesson video_link can be controlled by the Vimeo Player SDK.
 * @param {string} raw
 * @returns {boolean}
 */
export const isVimeoUrl = (raw) => parseVimeoUrl(raw) != null;
